import { Table as TableType, Row, flexRender } from '@tanstack/react-table';
import { ReactNode, Fragment } from 'react';
import { IconName } from '@optimus-web/icons';
import { IconButton } from '../../IconButton';
import { TableCell, TableCellInner } from '../styles';
import { TableBodyRow } from '../styles/style';

export const TableRow = <T,>({
    table,
    row,
    groupDisplayMode,
    rowSelectionEnabled,
    active = false,
    nextActive = false,
    prevActive = false,
    fixedFirstColumn = false,
    fixedLastColumn = false,
    renderSubcomponent,
    ...other
}: {
    table: TableType<T>;
    row: Row<T>;
    rowSelectionEnabled: boolean;
    groupDisplayMode: 'groupRows';
    active?: boolean;
    nextActive?: boolean;
    prevActive?: boolean;
    fixedFirstColumn?: boolean;
    fixedLastColumn?: boolean;
    renderSubcomponent?: (row: Row<T>) => ReactNode;
}) => {
    return (
        <Fragment key={row.id}>
            <TableBodyRow
                data-test-table-row={row.id}
                active={active}
                nextIsActive={nextActive}
                previousIsActive={prevActive}
                {...other}
            >
                {row.getVisibleCells().map((cell, cellIndex) => {
                    /**
                     * Grouping column is the column we use for rendering groups in when groupDisplayMode is 'groupRows'.
                     * It's either first column, or second one if row selection is enabled.
                     */
                    const isGroupingColumn = rowSelectionEnabled ? cellIndex === 1 : cellIndex === 0;
                    /**
                     * Leaf rows are the actual value rows when grouping on columns is applied.
                     */
                    const isLeafRow = !row.getIsGrouped() && row.depth > 0;

                    let renderedContent: ReactNode;
                    if (groupDisplayMode === 'groupRows' && isGroupingColumn && row.getIsGrouped()) {
                        /**
                         * Instead of the cell value, we display a value from grouped column based on the depth.
                         * This makes the first column to display all the groupings on grouped rows.
                         */
                        const columnId = table.getState().grouping[row.depth];
                        if (columnId) {
                            const column = table.getColumn(columnId);
                            renderedContent = flexRender(column?.columnDef.aggregatedCell, {
                                ...cell.getContext(),
                                column,
                                getValue: () => row.getValue(columnId),
                            });
                        }
                    } else if (cell.getIsAggregated()) {
                        renderedContent = flexRender(cell.column.columnDef.aggregatedCell, cell.getContext());
                    } else {
                        renderedContent = flexRender(cell.column.columnDef.cell, cell.getContext());
                    }

                    return (
                        <TableCell
                            key={cell.id}
                            data-test-table-cell={cell.id}
                            rowSelectionEnabled={rowSelectionEnabled}
                            fixedFirstColumn={fixedFirstColumn}
                            fixedLastColumn={fixedLastColumn}
                        >
                            <TableCellInner
                                align={cell.column.columnDef.meta?.align}
                                depth={isGroupingColumn && (row.getIsGrouped() || isLeafRow) ? row.depth : undefined}
                                leafRow={isLeafRow}
                            >
                                {row.getIsGrouped() && isGroupingColumn ? (
                                    <IconButton
                                        onClick={row.getToggleExpandedHandler()}
                                        type="button"
                                        icon={row.getIsExpanded() ? IconName.ChevronDown : IconName.ChevronRight}
                                        variant="ghost"
                                    />
                                ) : null}
                                {renderedContent}
                            </TableCellInner>
                        </TableCell>
                    );
                })}
            </TableBodyRow>
            {renderSubcomponent?.(row)}
        </Fragment>
    );
};
